import { createSelector } from "@reduxjs/toolkit";
import { parseValidDate, startOfToday, getHiddenFromUsersConfig, shouldHideByTagsFromUsers } from "../../utils/utils";

const toValidDate = (v) => {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
};
const now = () => Date.now();

const normalize = (v, type) => {
    if (v === undefined || v === null)
        return v;
    switch (type) {
        case "date":
            return v ? new Date(v).getTime() : v;
        case "number":
            return Number(v);
        case "string":
        default:
            return typeof v === "string" ? v.toLowerCase() : v;
    }
};

const compareValues = (a, b, { direction = "asc", nulls = "last", type = "string" } = {}) => {
    const dir = direction === "desc" ? -1 : 1;
    const aNull = a === undefined || a === null || a === "";
    const bNull = b === undefined || b === null || b === "";
    if (aNull && bNull)
        return 0;
    if (aNull)
        return nulls === "first" ? -1 : 1;
    if (bNull)
        return nulls === "first" ? 1 : -1;
    const aNormalize = normalize(a, type);
    const bNormalize= normalize(b, type);
    if (aNormalize < bNormalize)
        return -1 * dir;
    if (aNormalize > bNormalize)
        return 1 * dir;
    return 0;
};

export const selectEntities = createSelector(
    [
        (state) => state?.entities?.list,
        (state) => state?.auth?.isStudent,
    ],
    (list, isStudent) => {
        if (!list)
            return [];
        const hidden = getHiddenFromUsersConfig(isStudent);
        if (!hidden)
            return list;
        return list.filter(item => !shouldHideByTagsFromUsers(item, hidden));
    }
);

export const matchesValue = (fieldValue, filterValue) => {
    if (Array.isArray(filterValue) && filterValue.length === 0) {
        return Array.isArray(fieldValue) && fieldValue.length === 0;
    }
    if (Array.isArray(filterValue)) {
        if (Array.isArray(fieldValue)) {
            return fieldValue.some(fv => filterValue.includes(fv));
        }
        return filterValue.includes(fieldValue);
    } else {
        if (Array.isArray(fieldValue)) {
            return fieldValue.includes(filterValue);
        }
        return fieldValue === filterValue;
    }
};

export const getFieldValue = (obj, path) => path?.split('.')?.reduce((current, key) => current?.[key], obj);

export const checkFilter = (item, filter) => {
    const { by, value, operator = 'equals' } = filter;
    const fieldValue = getFieldValue(item, by);

    switch (operator) {
        case 'equals':
            return matchesValue(fieldValue, value);

        case 'not_equals':
            return !matchesValue(fieldValue, value);

        case 'contains':
            if (typeof fieldValue === 'string' && typeof value === 'string') {
                return fieldValue.toLowerCase().includes(value.toLowerCase());
            }
            return matchesValue(fieldValue, value);

        case 'not_contains':
            if (typeof fieldValue === 'string' && typeof value === 'string') {
                return !fieldValue.toLowerCase().includes(value.toLowerCase());
            }
            return !matchesValue(fieldValue, value);

        case 'exists':
            return fieldValue !== undefined && fieldValue !== null && fieldValue !== '';

        case 'not_exists':
            return fieldValue === undefined || fieldValue === null || fieldValue === '';

        case 'in':
            return Array.isArray(value) && matchesValue(fieldValue, value);
        case 'not_in':
            return Array.isArray(value) && !matchesValue(fieldValue, value);
        case "date_gte_now":
            return !!toValidDate(fieldValue) && toValidDate(fieldValue)?.getTime() >= now();
        case "now_between": {
            const { fromBy, toBy } = value || {};
            if (!fromBy || !toBy)
                return false;
            const from = toValidDate(getFieldValue(item, fromBy));
            const to = toValidDate(getFieldValue(item, toBy));
            if (!from || !to)
                return false;
            const t = now();
            return t >= from.getTime() && t <= to.getTime();
        }

        case "event_not_passed": {
            if (getFieldValue(item, "allDayEvent")) {
                const d = parseValidDate(getFieldValue(item, "date"));
                return !!d && d.getTime() >= startOfToday().getTime();
            }
            const to = parseValidDate(getFieldValue(item, "to"));
            return !!to && to.getTime() >= now();
        }
        case "event_active_now": {
            if (getFieldValue(item, "allDayEvent")) return false;
            const from = parseValidDate(getFieldValue(item, "from"));
            const to = parseValidDate(getFieldValue(item, "to"));
            if (!from || !to)
                return false;
            const t = now();
            return t >= from.getTime() && t <= to.getTime();
        }

        case "student_grade": {
            const grade = Number(value);
            if (value == null || value === "" || Number.isNaN(grade))
                return true;
            const fromClass = item.fromClass;
            if (fromClass == null || fromClass === "")
                return false;
            return fromClass <= grade && item.toClass >= grade;
        }

        case "class_range_overlap": {
            const { from, to } = value || {};
            if (from == null || to == null) return true;
            if (item.fromClass == null || item.toClass == null) return true;
            return Number(item.fromClass) <= to && Number(item.toClass) >= from;
        }

        case "calendar_assessment_school": {
            if (!getFieldValue(item, "isAssessment")) return true;
            const entitySchoolId = getFieldValue(item, "schoolId");
            if (entitySchoolId == null) return true;
            return Number(entitySchoolId) === Number(value);
        }

        default:
            return matchesValue(fieldValue, value);
    }
};

export const evaluateFilter = (item, filterOrGroup) => {
    if (filterOrGroup?.group && Array.isArray(filterOrGroup.group)) {
        const op = (filterOrGroup.op || 'OR').toUpperCase();
        if (op === 'AND')
            return filterOrGroup.group.every(f => evaluateFilter(item, f));
        return filterOrGroup.group.some(f => evaluateFilter(item, f));
    }
    return checkFilter(item, filterOrGroup);
};

export const sortBySpec = (list = [], sortSpec = []) => {
    if (!Array.isArray(sortSpec) || sortSpec.length === 0) 
        return list;
    const copy = [...list];
    copy.sort((x, y) => {
        for (const s of sortSpec) {
            const xv = getFieldValue(x, s.by);
            const yv = getFieldValue(y, s.by);
            const c = compareValues(xv, yv, s);
            if (c !== 0) return c;
        }
        return 0;
    });

    return copy;
};

export const selectEntitiesByFilters = createSelector(
    [
        selectEntities,
        (state, query) => query,
    ], (entities, query) => {
        const filters = query?.filters || query;
        const sort = query?.sort;
        if (!entities)
            return [];
        let result = entities;
        if (Array.isArray(filters) && filters.length === 0)
            return [];
        else {
            if (Array.isArray(filters) && filters.length > 0)
                result = result.filter(item => filters.every(f => evaluateFilter(item, f)));
            if (query?.dedup) {
                const { by, prefer } = query.dedup;
                const map = new Map();
                result.forEach(item => {
                    const key = getFieldValue(item, by);
                    if (!map.has(key) || getFieldValue(item, prefer.by) === prefer.value)
                        map.set(key, item);
                });
                result = [...map.values()];
            }
            if (Array.isArray(sort) && sort.length > 0)
                result = sortBySpec(result, sort);
        }
        return result;
    }
);
